import { useState } from 'react';
import { Spinner } from '../../Spinner';
import {
  canAddAccount,
  connectorDescriptionKey,
  connectorDisplayName,
  showsAccountList,
  supportsMultipleAccounts,
  type ConnectorStatus,
} from '../../../lib/connectors';
import { useTranslation } from '../../../lib/i18n';
import { connectorsTargetElementId } from '../../../lib/connectors-route';
import { useProductName } from '../../../lib/use-brand';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { CONNECTORS_PAGE } from '../../../lib/use-connectors';
import { useNarrow } from '../../../lib/use-narrow';
import { ConnectorLogo } from './ConnectorLogo';
import { AccountAvatar } from './AccountAvatar';
import { ChevronDownIcon, PlusIcon, TrashIcon, UnlinkIcon } from './icons';
import styles from './connectors.module.css';
import { Badge, Button, IconButton, TextButton } from '../ui';
import { shopifyHealthLabelKey } from '../../../lib/shopify-health';
import { ShopifyConnectionHealth } from './ShopifyConnectionHealth';

function useConnectorDescription(connector: ConnectorStatus): string | undefined {
  const { t } = useTranslation();
  const productName = useProductName();
  const key = connectorDescriptionKey(connector);
  if (key) {
    const localized = t(key, { productName });
    if (localized && localized !== key) return localized;
  }
  return connector.description || undefined;
}

/**
 * The card's trailing controls (Figma 14013-149893): disconnect, then the account-list
 * chevron when there is a list to open. One cluster rendered in two places — bottom-right
 * of the header while collapsed, its own bottom row once expanded — so it always sits the
 * same distance off the card's bottom edge.
 */
function CardActions({
  connector,
  name,
  busy,
  accountsId,
  expanded,
  onToggle,
  onDisconnect,
}: {
  connector: ConnectorStatus;
  name: string;
  busy: ReadonlySet<string>;
  accountsId?: string;
  expanded: boolean;
  onToggle?: () => void;
  onDisconnect: (connector: ConnectorStatus, name: string, accountId?: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className={styles.cardActions}>
      <IconButton
        size="tiny"
        onClick={() => onDisconnect(connector, name)}
        loading={busy.has(connector.toolkit)}
        title={t('common.disconnect')}
        aria-label={t('common.disconnect')}
      >
        <UnlinkIcon />
      </IconButton>
      {onToggle && (
        <IconButton
          size="tiny"
          className={expanded ? `${styles.chevron} ${styles.chevronOpen}` : styles.chevron}
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={accountsId}
          title={expanded ? t('common.collapse') : t('common.expand')}
          aria-label={expanded ? t('common.collapse') : t('common.expand')}
        >
          <ChevronDownIcon />
        </IconButton>
      )}
    </div>
  );
}

export function ConnectorCard({
  connector,
  busy,
  multiAccountEnabled,
  onConnect,
  onDisconnect,
  onAddAnother,
}: {
  connector: ConnectorStatus;
  /** The keys with an action in flight. The card asks ONLY about its own toolkit and its own accounts —
   *  never whether the set is non-empty. A connect is an OAuth popup the user drives, so every other
   *  card has to stay live while it is open. Asking the set whether it is EMPTY is what greyed out the
   *  whole screen on one tap; both phones were already per-row. */
  busy: ReadonlySet<string>;
  /** The server's `multiAccountEnabled` — the gate native itemizes accounts behind. */
  multiAccountEnabled: boolean;
  onConnect: (connector: ConnectorStatus) => void;
  onDisconnect: (connector: ConnectorStatus, name: string, accountId?: string) => void;
  onAddAnother: (connector: ConnectorStatus) => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const narrow = useNarrow();
  // Every card opens closed (Figma 14013-149893): a grid row of cards reads as a list of
  // tools, not a wall of account lists. Local rather than persisted — native's deck does
  // the same.
  const [expanded, setExpanded] = useState(false);
  const name = connectorDisplayName(connector);
  const description = useConnectorDescription(connector);
  // The DS Button/IconButton already disable themselves while `loading`, so each control is inert for
  // exactly its own action and for nothing else.
  const connectBusy = busy.has(connector.toolkit);
  const { connected } = connector;
  // A refused authorization still has a row, so `connected` alone would draw the checkmark over a
  // calendar that answers nothing. Retrying is what these owners do instead of reconnecting, so the
  // card has to say which one it is.
  const needsReconnect = connected && connector.needsReconnect === true;
  const accounts = connector.accounts ?? [];
  // WHICH accounts are itemized is native's rule (`showsAccountList`): a connected row lists whatever the
  // payload carries, labelled or not. WHETHER the row is expandable also needs an account to expand to —
  // the design QA asks for the chevron "only if there is an account", and native's rule alone would give a
  // 0-account row a chevron opening an empty list.
  const canList = supportsMultipleAccounts(connector);
  const isMultiAccount = connected && canList && accounts.length > 1;
  const showAccountList = showsAccountList(connector, multiAccountEnabled) && accounts.length > 0;
  const canAddAnotherAccount = connector.toolkit !== 'google-apps-script' && canAddAccount(accounts.length, connector.maxAccounts);
  const secondary = isMultiAccount ? t('connectors.accounts.summary', { count: accounts.length }) : description;
  const staleAccountId = !connected && accounts.length === 1 ? accounts[0].id : undefined;
  // No list to open, no chevron — a connector with a single unlabelled account keeps just
  // the disconnect.
  const accountsId = showAccountList ? `connector-accounts-${connector.toolkit}` : undefined;
  const accountsOpen = showAccountList && expanded;
  const toggleAccounts = showAccountList
    ? () => {
        logPageClickBi({
          ...CONNECTORS_PAGE,
          button_name: `accounts_${connector.toolkit}`,
          action: accountsOpen ? 'collapse' : 'expand',
        });
        setExpanded(!accountsOpen);
      }
    : undefined;
  const statusBadge = needsReconnect ? (
    <Badge variant="waiting-on-you" label={t('connectors.reconnectNeeded')} className={styles.badge} />
  ) : connector.shopifyHealth && connector.shopifyHealth.state !== 'connected' ? (
    <Badge variant="waiting-on-you" label={t(shopifyHealthLabelKey(connector.shopifyHealth))} className={styles.badge} />
  ) : (
    <Badge variant="connected" label={t('common.connected')} className={styles.badge} />
  );
  const reconnect = needsReconnect || connector.toolkit === 'google-apps-script' ? (
    <div className={styles.reconnectRow}>
      <Button
        variant={narrow ? 'contained' : 'outlined'}
        size="small"
        onClick={() => onConnect(connector)}
        loading={connectBusy}
        className={styles.connect}
      >
        {connectBusy ? t('common.connecting') : connector.toolkit === 'google-apps-script' ? t('appsScript.setup.title') : t('common.reconnect')}
      </Button>
    </div>
  ) : null;
  const actions = (
    <CardActions
      connector={connector}
      name={name}
      busy={busy}
      accountsId={accountsId}
      expanded={accountsOpen}
      onToggle={toggleAccounts}
      onDisconnect={onDisconnect}
    />
  );

  if (!connected) {
    return (
      <div id={connector.toolkit === 'shopify-direct' ? connectorsTargetElementId('tools', connector.toolkit) : undefined} className={styles.card}>
        <div className={styles.main}>
          <ConnectorLogo connector={connector} name={name} size={40} />
          <div className={styles.text}>
            <div className={`sym-h4 ${styles.name}`}>{name}</div>
            {description && <div className={styles.desc}>{description}</div>}
          </div>
          <div className={styles.aside}>
            <Button
              variant={narrow ? 'contained' : 'outlined'}
              size={narrow ? 'small' : 'medium'}
              onClick={() => onConnect(connector)}
              loading={connectBusy}
              className={styles.connect}
            >
              {connectBusy ? t('common.connecting') : t('common.connect')}
            </Button>
            {/* A row can carry accounts while reading as not-connected when its authorization never
                finished or has lapsed. Without this the user can neither use it nor clear it, and each
                retry leaves another half-finished connection behind. Scoping the busy key to the lone
                account keeps the Connect button beside it from reading "Connecting…" during a removal. */}
            {accounts.length > 0 && (
              <IconButton
                size="small"
                onClick={() => onDisconnect(connector, name, staleAccountId)}
                loading={busy.has(staleAccountId ?? connector.toolkit)}
                title={t('common.disconnect')}
                aria-label={t('common.disconnect')}
              >
                <UnlinkIcon />
              </IconButton>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id={connector.toolkit === 'shopify-direct' ? connectorsTargetElementId('tools', connector.toolkit) : undefined} className={`${styles.card} ${styles.cardConnected}`} data-connected data-needs-reconnect={needsReconnect || undefined}>
      {narrow ? (
        /* Mobile (Figma 11660-59339): one row — logo, then title/subtitle, then the
           "Connected" badge. The disconnect stays as a trailing ghost icon (the
           static design omits it, but it's the only disconnect for a connector
           with no account list). */
        <>
          <div className={styles.connectedRow}>
            <ConnectorLogo connector={connector} name={name} size={28} glyphSize={18} />
            <div className={styles.text}>
              <div className={styles.title}>{name}</div>
              {secondary && <div className={styles.desc}>{secondary}</div>}
              {connector.notice && <div className={styles.desc}>{connector.notice}</div>}
              {connector.shopifyHealth && <ShopifyConnectionHealth health={connector.shopifyHealth} />}
            </div>
            {statusBadge}
            {!accountsOpen && actions}
          </div>
          {reconnect}
        </>
      ) : (
        <div className={styles.connectedTop}>
          <div className={styles.connectedHead}>
            <ConnectorLogo connector={connector} name={name} size={28} glyphSize={18} />
            {statusBadge}
          </div>
          <div className={styles.connectedBody}>
            <div className={styles.text}>
              <div className={styles.title}>{name}</div>
              {secondary && <div className={styles.desc}>{secondary}</div>}
              {connector.notice && <div className={styles.desc}>{connector.notice}</div>}
              {connector.shopifyHealth && <ShopifyConnectionHealth health={connector.shopifyHealth} />}
            </div>
            {!accountsOpen && actions}
          </div>
          {reconnect}
        </div>
      )}

      {accountsOpen && (
        <div className={styles.accountsBlock}>
          <div id={accountsId} className={styles.accounts}>
            <div className={styles.divider} aria-hidden />
            {accounts.map((account, index) => {
              const label = account.label || t('connectors.accountFallback', { number: index + 1 }) || '';
              return (
                <div key={account.id} className={styles.accountRow}>
                  <AccountAvatar label={label} />
                  <div className={styles.accountText}>
                    <span className={styles.accountLabel}>{label}</span>
                    {/* The optional human/workspace line ("Work, Pizza Milano" / "Personal") — only some
                        connectors carry it, so it renders only when present. */}
                    {account.secondaryLabel && <span className={styles.accountSub}>{account.secondaryLabel}</span>}
                  </div>
                  {/* The design draws this trash in the standard ink, not the destructive red: it opens
                      a confirm rather than removing on the spot (Figma 14013-149893). */}
                  <IconButton
                    size="tiny"
                    onClick={() => onDisconnect(connector, account.label || name, account.id)}
                    loading={busy.has(account.id)}
                    aria-label={t('connectors.mcp.removeNamed', { name: label })}
                    title={t('common.disconnect')}
                  >
                    <TrashIcon />
                  </IconButton>
                </div>
              );
            })}
            {canAddAnotherAccount && (
              <TextButton
                size="tiny"
                className={styles.addLink}
                onClick={() => onAddAnother(connector)}
                disabled={connectBusy}
                startIcon={connectBusy ? <Spinner /> : <PlusIcon size={16} />}
              >
                {t('connectors.addAccount')}
              </TextButton>
            )}
          </div>
          <div className={styles.footerActions}>{actions}</div>
        </div>
      )}
    </div>
  );
}
